import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { io } from "socket.io-client";

const SOCKET_URL =
  process.env.REACT_APP_API_URL?.replace("/api/", "") ||
  "https://snap-tournament.onrender.com";

function SnapDraftRoom({ user }) {
  const { roomId } = useParams();
  const navigate = useNavigate();
  const [socket, setSocket] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [creatorId, setCreatorId] = useState(null);
  const [cardsPerPlayer, setCardsPerPlayer] = useState(6);
  const [loading, setLoading] = useState(true);
  const [draftName, setDraftName] = useState("");
  const [numDecks, setNumDecks] = useState(8);
  const [usageLimit, setUsageLimit] = useState(3);

  useEffect(() => {
    const s = io(SOCKET_URL, { transports: ["websocket"] });
    setSocket(s);
    s.emit("joinRoom", { roomId, user });
    s.on("participantsUpdated", (list) => setParticipants(list));
    s.on("cardsLimitUpdated", (limit) => setCardsPerPlayer(limit));
    s.on("draftStarted", ({ roomId }) => {
      navigate(`/draft/live/${roomId}`);
    });

    fetch(`${process.env.REACT_APP_API_URL}api/draft/room/${roomId}/state`)
      .then((res) => res.json())
      .then((data) => {
        setParticipants(data.participants || []);
        setCreatorId(data.creatorId);
        setCardsPerPlayer(data.cardsPerPlayer || 6);
        setLoading(false);
      })
      .catch(console.error);

    return () => s.disconnect();
  }, [roomId, user]);

  const isCreator = creatorId === user.id;

  const handleLimitChange = async (e) => {
    const newLimit = Number(e.target.value);
    setCardsPerPlayer(newLimit);

    try {
      await fetch(`${process.env.REACT_APP_API_URL}api/draft/room/${roomId}/cards`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ cardsPerPlayer: newLimit }),
      });
      socket?.emit("updateCardsPerPlayer", { roomId, cardsPerPlayer: newLimit });
    } catch (err) {
      console.error("Error updating cards per player:", err);
    }
  };

  const handleStartDraft = () => {
    if (!draftName.trim()) {
      alert("Please enter a name for this draft before starting.");
      return;
    }

    socket.emit("startDraft", {
      roomId,
      draftName,
      numDecks,
      usageLimit,
    });
  };

  if (loading) return <div className="loading">Loading room...</div>;

  return (
    <div className="app-wrapper">
      <div className="styled-info-modal" style={{ maxWidth: "700px", margin: "40px auto" }}>
        <h1 className="header-title">Draft Room</h1>
        <p style={{ textAlign: "center", color: "var(--snap-muted)" }}>
          Room ID: <b style={{ color: "var(--snap-gold)" }}>{roomId}</b>
        </p>

        <h3 className="header-title" style={{ fontSize: "1.6rem" }}>
          Participants
        </h3>
        <ul style={{ listStyle: "none", padding: 0 }}>
          {participants.map((p) => (
            <li key={p.id} className="list-item">
              {p.userName || p.username}
            </li>
          ))}
        </ul>

        {/* Cards per player */}
        <div style={{ marginTop: "20px", textAlign: "center" }}>
          {isCreator ? (
            <div>
              <label style={{ marginRight: "10px" }}>Cards per player:</label>
              <input
                type="number"
                min="1"
                max="12"
                value={cardsPerPlayer}
                onChange={handleLimitChange}
                className="modern-input"
                style={{ width: "80px" }}
              />
            </div>
          ) : (
            <p style={{ color: "var(--snap-gold)" }}>
              Cards per player: <b>{cardsPerPlayer}</b>
            </p>
          )}
        </div>

        {/* --- Deck creation settings --- */}
        {isCreator && (
          <div style={{ textAlign: "center", marginTop: "20px" }}>
            <label style={{ display: "block", marginBottom: "6px" }}>
              Draft Name:
            </label>
            <input
              type="text"
              value={draftName}
              onChange={(e) => setDraftName(e.target.value)}
              placeholder="Enter draft name"
              className="modern-input"
              style={{ width: "80%", textAlign: "center" }}
            />

            {/* 🧩 Decks per player */}
            <div style={{ marginTop: "15px" }}>
              <label style={{ marginRight: "10px" }}>Decks per player:</label>
              <input
                type="number"
                min="1"
                max="16"
                value={numDecks}
                onChange={(e) => setNumDecks(Number(e.target.value))}
                className="modern-input"
                style={{ width: "80px" }}
              />
            </div>

            {/* 🔢 Card usage limit */}
            <div style={{ marginTop: "15px" }}>
              <label style={{ marginRight: "10px" }}>
                Card usage limit per deck:
              </label>
              <input
                type="number"
                min="1"
                max="12"
                value={usageLimit}
                onChange={(e) => setUsageLimit(Number(e.target.value))}
                className="modern-input"
                style={{ width: "80px" }}
              />
            </div>
          </div>
        )}

        <div style={{ textAlign: "center", marginTop: "20px" }}>
          <button
            className="button"
            disabled={!isCreator}
            onClick={handleStartDraft}
            style={{
              opacity: isCreator ? 1 : 0.5,
              cursor: isCreator ? "pointer" : "not-allowed",
            }}
          >
            {isCreator ? "Start Draft" : "Waiting for Host..."}
          </button>
        </div>
      </div>
    </div>
  );
}

export default SnapDraftRoom;
